import Stepper from 'bs-stepper';
import { useRerender } from 'hooks/useRerender';
import { isEmpty } from 'lodash';
import { Key, createContext, useContext, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import * as schedulesSelectors from '../../../Schedules/reducers';
import { AssignedUnit } from '../../hooks/useGetAssignedUnits';
import { ValidationError, duplicatedUnitRouteAssignmentValidator } from '../../utils';
import { EditableAssignmentFieldsWithNames } from './SlidingPaneEditAssignmentByDate';

// Types moved from deleted files
export enum AssignmentTypes {
  ByDate = 1,
  BySchedule = 2
}

export enum AlreadyLeftTheOrigin {
  Yes = 1,
  No = 0
}

// Unified form values type
export type AssignmentFormValues = {
  // Assignment Type
  assignmentType: AssignmentTypes;
  alreadyLeftTheOrigin: AlreadyLeftTheOrigin | undefined;
  globalNotificationFrec: string;
  useSharedLink: boolean;
  notificationEvents: string;
  globalNotificationEmails: string;
  notificationEmails: string;
  // Common Values
  startDate: string;
  scheduleId: number;
  scheduleName: string;
  startTimeWindow: { totalSeconds: number };
  completionType: number;
  automaticMonitoringWhenArrivingAtOrigin: boolean;
  completionTime: { totalSeconds: number };
  isSpecialCargo: boolean;
  speedLimit: number;
};

export interface UnitsRoutesAssignment {
  correlative?: number;
  unitId: number;
  routeId: number;
  customerId: number | null;
  unitName: string | null;
  unitMainStateId?: number | null;
  unitMainStateName?: string | null;
  routeName: string | null;
  customerName: string | null;
  customerLocationId: number | null;
  startDate?: null | string | Date;
  // Additional fields that can be set by user in step 2
  scheduleId?: number | null;
  scheduleName?: string;
  scheduleNextRunDate?: string;
  driverId?: null | number;
  driverName?: string | null;
  driverPrivateCode?: string;
  completionTimeByUserInSec?: number;
  automaticMonitoringWhenArrivingAtOrigin?: boolean;
  alreadyLeftTheOrigin?: number | boolean;
  startTimeWindowInSec?: number;
  isSpecialCargo?: boolean;
  speedLimit?: number;
  customField1?: null | string;
  customField2?: null | string;
  customField3?: null | string;
  customField4?: null | string;
  customField5?: null | string;
  customField6?: null | string;
  vesselName?: null | string;
  shipmentTypeId?: null | number;
  hasVgmCertificate?: null | boolean;
  vgmCertificateDescription?: null | string;
  isStc?: null | boolean;
  stcDescription?: null | string;
  driverExtraPhoneNumbers?: null | string;
  hasCustodian?: null | boolean;
  custodianName?: null | string;
  custodianPhoneNumber?: null | string;
  completionTimeInMinutes?: number;
  startTimeWindow?: number;
  completionType?: number;
  globalNotificationEmails?: string;
  globalNotificationFrec?: string;
  useSharedLink?: boolean;
  notificationEmails?: string;
  notificationEvents?: string;
  validationErrors?: ValidationError[];
}

// Unified Assignment type - works for both by-date and by-schedule assignments
export interface Assignment {
  correlative: number;
  unitId: number;
  unitName: string;
  routeId: number;
  routeName: string;
  customerId?: number | null;
  customerLocationId?: number | null;
  customerName?: string | null;
  globalNotificationEmails?: string;
  globalNotificationFrec?: string;
  useSharedLink?: boolean;
  notificationEmails?: string;
  notificationEvents?: string;
  startDate?: null | string | Date;
  scheduleId?: number | null; // null for by-date, number for by-schedule
  scheduleName?: string;
  scheduleNextRunDate?: string;
  driverId?: null | number;
  driverName?: string | null;
  driverPrivateCode?: string;
  completionTimeByUserInSec?: number;
  automaticMonitoringWhenArrivingAtOrigin?: boolean;
  alreadyLeftTheOrigin?: number | boolean;
  startTimeWindowInSec?: number;
  isSpecialCargo?: boolean;
  speedLimit?: number;
  customField1?: null | string;
  customField2?: null | string;
  customField3?: null | string;
  customField4?: null | string;
  customField5?: null | string;
  customField6?: null | string;
  vesselName?: null | string;
  shipmentTypeId?: null | number;
  hasVgmCertificate?: null | boolean;
  vgmCertificateDescription?: null | string;
  isStc?: null | boolean;
  stcDescription?: null | string;
  driverExtraPhoneNumbers?: null | string;
  hasCustodian?: null | boolean;
  custodianName?: null | string;
  custodianPhoneNumber?: null | string;
  opcAppointmentDate?: null | string | Date;
  billOfLading?: null | string;
  containerSize?: null | string;
  voyage?: null | string;
  completionTimeInMinutes?: number;
  startTimeWindow?: number;
  completionType?: number;
  validationErrors?: ValidationError[];
}

// Legacy type aliases for backward compatibility
export type AssignmentByDate = Assignment;
export type AssignmentBySchedule = Assignment;

export interface ValidationFilter {
  unitId: number;
  routeId: number;
  correlative: number;
}

interface AssignmentContextValue {
  resetForms: Key;
  startResetForms(): void;
  stepper: Stepper | null;
  setStepper(stepper: Stepper | null): void;
  formValues: Partial<AssignmentFormValues>;
  updateFormValues: (formValues: Partial<AssignmentFormValues>) => void;
  revalidateUnitRouteAssignments(): void;
  assignedUnits: AssignedUnit[];
  updateAssignedUnits: (assignedUnits: AssignedUnit[]) => void;
  assignments: Partial<Assignment>[]; // Computed from unitRouteAssignments + formValues
  unitRouteAssignments: UnitsRoutesAssignment[];
  updateUnitRouteAssignments: (unitRouteAssignments: UnitsRoutesAssignment[]) => void;
  updateAssignment: (partialChanges: Partial<Assignment>) => void;
  updateSelectedAssignments: (
    partialChanges: Partial<EditableAssignmentFieldsWithNames>,
    selected: Partial<Assignment>[]
  ) => void;
  removeUnitRouteAssignment: (unitId: number, routeId: number) => void;
  removeMultipleUnitRouteAssignments: (assignments: Array<{unitId: number, routeId: number}>) => void;
  resetAssignments(): void;
  removeAllAssignments(): void;
  removeAllCompletedAssignments(completedAssignments: Partial<UnitsRoutesAssignment>[]): void;
  shouldExecuteAssignment: boolean;
  setShouldExecuteAssignment(value: boolean): void;
}

export const AssignmentContext = createContext<AssignmentContextValue | null>(null);

export const AssignmentProvider = ({ children }) => {
  const [stepper, changeStepper] = useState<Stepper | null>(null);
  const [formValues, setFormValues] = useState<Partial<AssignmentFormValues>>({});
  const [assignedUnits, setAssignedUnits] = useState<AssignedUnit[]>([]);
  const [shouldExecuteAssignment, setShouldExecuteAssignment] = useState(false);

  const [unitRouteAssignments, setUnitRouteAssignments] = useState<UnitsRoutesAssignment[]>([]);

  const { rerender: resetForms, startRerender: startResetForms } = useRerender();
  const intl = useIntl();

  const { schedule } = useSelector((state) => ({
    schedule: schedulesSelectors.getSchedule(state, formValues.scheduleId)
  }));

  // Computed assignments - automatically derived from unitRouteAssignments + formValues
  // Merges the base unitRouteAssignments with form-level configuration
  const assignments = useMemo<Partial<Assignment>[]>(() => {
    const isScheduleMode = formValues.assignmentType === AssignmentTypes.BySchedule;

    return unitRouteAssignments.map((unitRouteAssignment) => {
      const data: Partial<Assignment> = {
        ...unitRouteAssignment,
        // Override/set form-level values ONLY if not already set in unitRouteAssignment
        globalNotificationFrec: unitRouteAssignment.globalNotificationFrec ?? formValues.globalNotificationFrec,
        globalNotificationEmails: unitRouteAssignment.globalNotificationEmails ?? formValues.globalNotificationEmails,
        useSharedLink: unitRouteAssignment.useSharedLink ?? formValues.useSharedLink,
        notificationEmails: unitRouteAssignment.notificationEmails ?? formValues.notificationEmails,
        notificationEvents: unitRouteAssignment.notificationEvents ?? formValues.notificationEvents,
        startDate: unitRouteAssignment.startDate ?? formValues.startDate,
        completionType: unitRouteAssignment.completionType ?? formValues.completionType,
        automaticMonitoringWhenArrivingAtOrigin:
          unitRouteAssignment.automaticMonitoringWhenArrivingAtOrigin ??
          formValues.automaticMonitoringWhenArrivingAtOrigin,
        speedLimit: unitRouteAssignment.speedLimit ?? formValues.speedLimit,
        alreadyLeftTheOrigin:
          unitRouteAssignment.alreadyLeftTheOrigin ??
          formValues.alreadyLeftTheOrigin === AlreadyLeftTheOrigin.Yes,
        isSpecialCargo: unitRouteAssignment.isSpecialCargo ?? formValues.isSpecialCargo,
        completionTimeInMinutes:
          unitRouteAssignment.completionTimeInMinutes ??
          (formValues.completionTime?.totalSeconds
            ? formValues.completionTime?.totalSeconds / 60
            : undefined),
        startTimeWindow:
          unitRouteAssignment.startTimeWindow ??
          (formValues.startTimeWindow?.totalSeconds
            ? formValues.startTimeWindow?.totalSeconds / 60
            : undefined),
        // Schedule-specific fields (null for by-date mode)
        scheduleId:
          unitRouteAssignment.scheduleId ?? (isScheduleMode ? formValues.scheduleId : null),
        scheduleName:
          unitRouteAssignment.scheduleName ??
          (isScheduleMode ? formValues.scheduleName : undefined),
        // Initialize fields if not set
        driverId: unitRouteAssignment.driverId ?? null,
        completionTimeByUserInSec: unitRouteAssignment.completionTimeByUserInSec ?? 0,
        startTimeWindowInSec: unitRouteAssignment.startTimeWindowInSec ?? 0
      };

      return data;
    });
  }, [unitRouteAssignments, formValues]);

  const validators: ((...args: any[]) => ValidationError[])[] = useMemo(
    () => {
      return [duplicatedUnitRouteAssignmentValidator(assignedUnits, formValues, formValues, schedule)];
    },
    [assignedUnits, formValues, schedule]
  );

  const setStepper = (stepper: Stepper | null) => {
    changeStepper(stepper);
  };

  const updateFormValues = (newValues: Partial<AssignmentFormValues>) => {
    setFormValues((prevState) => ({ ...prevState, ...newValues }));
  };

  const revalidateUnitRouteAssignments = () => {
    const validatedItems: UnitsRoutesAssignment[] = validateAssignments(unitRouteAssignments);
    setUnitRouteAssignments(validatedItems);
  };

  const validateAssignments = (
    assignments:
      | UnitsRoutesAssignment[]
      | Partial<AssignmentByDate>[]
      | Partial<AssignmentBySchedule>[],
    validationFilters?: ValidationFilter
  ) =>
    assignments.map((assignment) => {
      let isValidationNeeded = !validationFilters || Object.keys(validationFilters).length === 0;
      for (const key in validationFilters) {
        if (validationFilters[key] !== undefined && assignment[key] === validationFilters[key]) {
          isValidationNeeded = true;
        }
      }

      if (isValidationNeeded) {
        // Preserve DB check errors (routes.routeSchedulingConflict) because local validators
        // cannot determine these without making a DB request
        const dbCheckErrors = assignment.validationErrors?.filter(
          error => error.type === 'routes.routeSchedulingConflict'
        ) || [];
        
        let validationErrors: ValidationError[] = [...dbCheckErrors];
        validators.forEach((validator) => {
          const validatorErrors = validator(assignments, assignment);
          if (validatorErrors) validationErrors.push(...validatorErrors);
        });
        return {
          ...assignment,
          validationErrors
        };
      }

      return assignment;
    });

  const updateAssignedUnits = (assignedUnits: AssignedUnit[]) => {
    const uniqueUnitIdValues = [...new Set(assignedUnits.map((item) => item.unitId))];
    setAssignedUnits((prevState) => {
      // Replace if updated info is received
      const oldStatus =
        prevState && prevState.length > 0
          ? prevState.filter((item) => !uniqueUnitIdValues.includes(item.unitId))
          : [];
      return [...oldStatus, ...assignedUnits];
    });
  };

  const updateUnitRouteAssignments = (providedAssignments: UnitsRoutesAssignment[]) => {
    const newAssignments = [...providedAssignments];

    const newData: UnitsRoutesAssignment[] = unitRouteAssignments.map((unitRouteAssignment) => {
      const newDataIdx = newAssignments.findIndex((newAssignment) => {
        const matches = newAssignment.unitId === unitRouteAssignment.unitId &&
          newAssignment.routeId === unitRouteAssignment.routeId;
        return matches;
      });

      if (newDataIdx > -1) {
        // get and remove item from newAssignments
        const newAssignment = newAssignments.splice(newDataIdx, 1)[0];
        // Use the new assignment's validation errors (from fresh DB check)
        // Don't preserve old validation errors as they may be outdated
        return newAssignment;
      }

      return unitRouteAssignment;
    });
    
    // First combine existing and new assignments temporarily
    const allAssignments = [...newData, ...newAssignments];
    
    // Validate new assignments against ALL assignments (existing + new)
    const validatedNewAssignments: UnitsRoutesAssignment[] = newAssignments.map((newAssignment) => {
      if (newAssignment.validationErrors !== undefined) {
        return newAssignment;
      }
      
      // Only run local validators if the assignment hasn't been through a DB check
      let validationErrors: ValidationError[] = [];
      validators.forEach((validator) => {
        const validatorErrors = validator(allAssignments, newAssignment);
        if (validatorErrors) validationErrors.push(...validatorErrors);
      });
      return {
        ...newAssignment,
        validationErrors
      };
    });
    
    // Combine existing (with preserved errors) and newly validated assignments
    const finalItems = [...newData, ...validatedNewAssignments];
    
    setUnitRouteAssignments(finalItems);
  };

  const removeUnitRouteAssignment = (unitId: number, routeId: number) => {
    const newData: UnitsRoutesAssignment[] = unitRouteAssignments.filter(
      (unitRouteAssignment) =>
        unitRouteAssignment.unitId !== unitId || unitRouteAssignment.routeId !== routeId
    );
    const validatedItems: UnitsRoutesAssignment[] = validateAssignments(newData);
    setUnitRouteAssignments(validatedItems);
  };

  const removeMultipleUnitRouteAssignments = (assignmentsToRemove: Array<{unitId: number, routeId: number}>) => {
    const newData: UnitsRoutesAssignment[] = unitRouteAssignments.filter(
      (unitRouteAssignment) =>
        !assignmentsToRemove.some(
          (toRemove) => toRemove.unitId === unitRouteAssignment.unitId && toRemove.routeId === unitRouteAssignment.routeId
        )
    );
    const validatedItems: UnitsRoutesAssignment[] = validateAssignments(newData);
    setUnitRouteAssignments(validatedItems);
  };

  const resetAssignments = () => {
    // Clear all user-specific fields from unitRouteAssignments
    setUnitRouteAssignments(
      unitRouteAssignments.map((item) => ({
        correlative: item.correlative,
        unitId: item.unitId,
        routeId: item.routeId,
        customerId: item.customerId,
        unitName: item.unitName,
        routeName: item.routeName,
        customerName: item.customerName,
        customerLocationId: item.customerLocationId,
        validationErrors: item.validationErrors
      }))
    );
  };

  const removeAllAssignments = () => {
    setUnitRouteAssignments([]);
  };

  const removeAllCompletedAssignments = (
    completedAssignments: Partial<UnitsRoutesAssignment>[]
  ) => {
    if (completedAssignments && completedAssignments.length > 0) {
      const validatedItems: UnitsRoutesAssignment[] = validateAssignments(
        unitRouteAssignments.filter(
          (item) =>
            completedAssignments.findIndex(
              (el) => el.routeId === item.routeId && el.unitId === item.unitId
            ) === -1
        )
      );
      setUnitRouteAssignments(validatedItems);
    }
  };

  // Update a single assignment - directly updates unitRouteAssignments
  const updateAssignment = (partialChanges: Partial<Assignment>) => {
    const validatedItems: UnitsRoutesAssignment[] = validateAssignments(
      unitRouteAssignments.map((item) => {
        if (item.correlative === partialChanges.correlative) {
          return { ...item, ...partialChanges };
        }
        return item;
      })
    );
    setUnitRouteAssignments(validatedItems);
  };

  // Update multiple selected assignments - directly updates unitRouteAssignments
  const updateSelectedAssignments = (
    partialChanges: Partial<EditableAssignmentFieldsWithNames>,
    selected: Partial<Assignment>[]
  ) => {
    const validatedItems: UnitsRoutesAssignment[] = validateAssignments(
      unitRouteAssignments.map((item) => {
        const isSelected = selected.some((s) => s.correlative === item.correlative);
        if (isSelected) {
          return { ...item, ...partialChanges } as UnitsRoutesAssignment;
        }
        return item;
      }) as UnitsRoutesAssignment[]
    );
    setUnitRouteAssignments(validatedItems);
  };

  const value: AssignmentContextValue = {
    resetForms,
    startResetForms,
    stepper,
    setStepper,
    formValues,
    updateFormValues,
    revalidateUnitRouteAssignments,
    assignedUnits,
    updateAssignedUnits,
    // Note: assignments is now computed/derived from unitRouteAssignments + formValues
    // It automatically recalculates when any of those dependencies change
    assignments,
    updateSelectedAssignments,
    updateAssignment,
    resetAssignments,
    removeAllAssignments,
    removeAllCompletedAssignments,
    updateUnitRouteAssignments,
    unitRouteAssignments,
    removeUnitRouteAssignment,
    removeMultipleUnitRouteAssignments,
    shouldExecuteAssignment,
    setShouldExecuteAssignment
  };

  if (!AssignmentContext) {
    throw new Error('useAssignmentContext must be used within a AssignmentProvider');
  }

  return <AssignmentContext.Provider value={value}>{children}</AssignmentContext.Provider>;
};

export const useAssignmentContext = () => {
  const context = useContext(AssignmentContext);
  if (context === null) {
    throw new Error('useAssignmentContext must be used within a AssignmentProvider');
  }
  return context;
};
